// src/constants/lanes.js

import { TAG_CATALOG } from "../utils/predefinedTags";

/**
 * Service lanes group incident categories into the domains we actually staff.
 *
 * Adding a lane (Shopify, infrastructure, deliverability, ...) is a data change
 * here plus its categories in TAG_CATALOG — no component changes required.
 * Lanes marked "planned" are visible to internal tooling but are not offered to
 * customers at intake, so we can build a lane out before selling it.
 */

export const LANE_STATUS = {
  ACTIVE: "active",
  PLANNED: "planned",
};

export const SERVICE_LANES = [
  {
    id: "payments-stripe",
    label: "Payments & Stripe",
    shortLabel: "Payments",
    status: LANE_STATUS.ACTIVE,
    description:
      "Stripe webhooks and event delivery, subscription and billing logic, checkout and access sync, Connect and payouts.",
    categoryIds: [
      "stripe-webhooks-events",
      "subscriptions-billing-logic",
      "checkout-payments-access",
      "connect-payouts-reliability",
    ],
    /**
     * Categories sitting directly on the revenue path: a failure here is losing
     * the customer money while we look at it, so severity is raised one level
     * above what the reported urgency alone would give.
     */
    revenueCriticalCategoryIds: [
      "checkout-payments-access",
      "connect-payouts-reliability",
    ],
  },
];

const LANE_BY_ID = new Map(SERVICE_LANES.map((lane) => [lane.id, lane]));

const LANE_BY_CATEGORY_ID = new Map(
  SERVICE_LANES.flatMap((lane) =>
    lane.categoryIds.map((categoryId) => [categoryId, lane])
  )
);

const CATEGORY_LABEL_BY_ID = new Map(
  TAG_CATALOG.map((category) => [category.id, category.label])
);

export function getLaneById(laneId) {
  return LANE_BY_ID.get(laneId) || null;
}

export function getLaneForCategory(categoryId) {
  return LANE_BY_CATEGORY_ID.get(categoryId) || null;
}

export function getActiveLanes() {
  return SERVICE_LANES.filter((lane) => lane.status === LANE_STATUS.ACTIVE);
}

/** Category IDs a customer is allowed to choose at intake today. */
export function getActiveCategoryIds() {
  return getActiveLanes().flatMap((lane) => lane.categoryIds);
}

export function isRevenueCriticalCategory(categoryId) {
  const lane = getLaneForCategory(categoryId);
  if (!lane) return false;
  return (lane.revenueCriticalCategoryIds || []).includes(categoryId);
}

/** Human-readable category name from TAG_CATALOG, with a safe fallback. */
export function getCategoryLabel(categoryId) {
  return CATEGORY_LABEL_BY_ID.get(categoryId) || "Uncategorised";
}
